import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ColorDot from 'src/components/ColorDot';
import ProductAvailabilityLabel from 'src/components/productAvailabilityLabel';
import RootStore from 'src/stores/RootStore';
import { TProductModel } from 'src/stores/types/ProductTypes';
import { DEFAULT_FRAME_CODES } from 'src/utils/constants';

interface SelectFrameProps {
    store?: typeof RootStore.Type;
    selectedFrameCode: string;
    setSelectedFrameCode: (selected: string) => void;
}

const SelectFrame = inject('store')(
    observer(({ store, selectedFrameCode, setSelectedFrameCode }: SelectFrameProps) => {
        const frameList: TProductModel[] | [] = store?.products.frameList;
        const [isOpen, setOpen] = useState(false);

        useEffect(() => {
            if (frameList?.length) {
                // A frame cover is mandatory, so always preselect one that can be ordered:
                // the default frame cover, otherwise one in stock, otherwise the first orderable one
                const isOrderable = (part: TProductModel) =>
                    part.productInformation.inStock || !!part.productInformation.availableFrom;
                const defaultValue =
                    frameList.find(part => DEFAULT_FRAME_CODES.has(part.id) && isOrderable(part)) ??
                    frameList.find(part => part.productInformation.inStock) ??
                    frameList.find(isOrderable);
                if (defaultValue) {
                    setSelectedFrameCode(defaultValue.id);
                } else {
                    setSelectedFrameCode('empty');
                }
            }
        }, [frameList]);

        if (!frameList?.length) {
            return null;
        }

        const getOption = (value: string) => {
            const foundOption = frameList.find(part => part.id === value);
            return foundOption ? <ColorDot color={foundOption.productInformation.color ?? ''} label /> : <></>;
        };

        return (
            <FormControl variant="standard" sx={{ margin: '10px 0 10px', width: '100%' }}>
                <InputLabel htmlFor="frame-item">
                    <FormattedMessage id="select.frame.color" />
                </InputLabel>
                <Select
                    data-testid="frameSelector"
                    labelId="frame-item"
                    open={isOpen}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={selectedFrameCode}
                    name="selectedFrameColor"
                    displayEmpty={false}
                    renderValue={(value: any) => getOption(value)}
                    onChange={e => setSelectedFrameCode(e.target.value as string)}
                    sx={{ overflow: 'hidden' }}
                >
                    {frameList.map((part: TProductModel) => (
                        <MenuItem
                            key={part.id}
                            value={part.id}
                            data-testid={`frame-${part.id}`}
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                            disabled={!part.productInformation.inStock && !part.productInformation.availableFrom}
                        >
                            <ColorDot color={part.productInformation.color ?? ''} label />
                            <ProductAvailabilityLabel
                                inStock={part.productInformation.inStock}
                                availableFrom={part.productInformation.availableFrom}
                                stockQuantity={part.productInformation.stockQuantity}
                            />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }),
);

export default SelectFrame;
